import React, { useEffect, useState, Children } from 'react';
import { Paintbrush, Building2, Hammer, Layers, ArrowRight, Zap, Droplets, Home } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { motion, useAnimation } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { ServiceModal } from './ServiceModal';
export function ServiceGrid() {
  const {
    t
  } = useLanguage();
  const controls = useAnimation();
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });
  const [activeService, setActiveService] = useState<number | null>(null);
  useEffect(() => {
    if (inView) {
      controls.start('visible');
    }
  }, [controls, inView]);
  const services = [{
    icon: Paintbrush,
    title: t.services.residential.title,
    desc: t.services.residential.desc,
    full: t.services.residential.full,
    accent: 'brand-red'
  }, {
    icon: Building2,
    title: t.services.commercial.title,
    desc: t.services.commercial.desc,
    full: t.services.commercial.full,
    accent: 'brand-blue'
  }, {
    icon: Hammer,
    title: t.services.remodeling.title,
    desc: t.services.remodeling.desc,
    full: t.services.remodeling.full,
    accent: 'brand-red'
  }, {
    icon: Layers,
    title: t.services.drywall.title,
    desc: t.services.drywall.desc,
    full: t.services.drywall.full,
    accent: 'brand-blue'
  }, {
    icon: Home,
    title: t.services.roofing.title,
    desc: t.services.roofing.desc,
    full: t.services.roofing.full,
    accent: 'brand-red'
  }, {
    icon: Zap,
    title: t.services.electrical.title,
    desc: t.services.electrical.desc,
    full: t.services.electrical.full,
    accent: 'brand-blue'
  }, {
    icon: Droplets,
    title: t.services.plumbing.title,
    desc: t.services.plumbing.desc,
    full: t.services.plumbing.full,
    accent: 'brand-red'
  }];
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 30
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: 'easeOut'
      }
    }
  };
  return <>
      <section id="services" className="py-24 bg-gray-50 relative" data-id="element-172">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-173">
          <div className="text-center mb-16" data-id="element-174">
            <h2 className="text-3xl md:text-4xl font-heading font-extrabold text-brand-black mb-3" data-id="element-175">
              {t.services.title}
            </h2>
            <p className="text-lg text-gray-600 font-medium mb-4" data-id="element-176">
              {t.services.subtitle}
            </p>
            <div className="w-24 h-1.5 bg-brand-red mx-auto rounded-full" data-id="element-177"></div>
          </div>

          <motion.div ref={ref} variants={containerVariants} initial="hidden" animate={controls} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-178">
            {services.map((service, index) => {
            const Icon = service.icon;
            const isRed = service.accent === 'brand-red';
            return <motion.div key={index} variants={itemVariants} className={`bg-white rounded-xl p-7 shadow-lg hover:shadow-xl transition-shadow border border-gray-100 group ${index === 6 ? 'md:col-span-2 lg:col-span-1' : ''}`} data-id="element-179">
                  <div className="flex items-start gap-5" data-id="element-180">
                    <div className={`p-3.5 rounded-lg transition-colors duration-300 ${isRed ? 'bg-red-50 group-hover:bg-brand-red' : 'bg-blue-50 group-hover:bg-brand-blue'}`} data-id="element-181">
                      <Icon className={`w-7 h-7 transition-colors duration-300 ${isRed ? 'text-brand-red group-hover:text-white' : 'text-brand-blue group-hover:text-white'}`} data-id="element-182" />
                    </div>
                    <div className="flex-1" data-id="element-183">
                      <h3 className="text-xl font-heading font-bold text-brand-black mb-2" data-id="element-184">
                        {service.title}
                      </h3>
                      <p className="text-gray-600 mb-5 leading-relaxed text-sm" data-id="element-185">
                        {service.desc}
                      </p>
                      <button onClick={() => setActiveService(index)} className={`inline-flex items-center gap-2 font-bold hover:opacity-80 transition-colors text-sm cursor-pointer ${isRed ? 'text-brand-red' : 'text-brand-blue'}`} data-id="element-186">
                        {t.services.cta}
                        <ArrowRight className="w-4 h-4" data-id="element-187" />
                      </button>
                    </div>
                  </div>
                </motion.div>;
          })}
          </motion.div>
        </div>
      </section>
      {activeService !== null && (() => {
      const ActiveIcon = services[activeService].icon;
      return <ServiceModal isOpen={true} onClose={() => setActiveService(null)} title={services[activeService].title} content={services[activeService].full} icon={<ActiveIcon className="w-6 h-6" data-id="element-189" />} data-id="element-188" />;
    })()}
    </>;
}